import { Component, ElementRef, inject, ViewChild, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ServidorPublico } from '../../../../models/ServidorPublico';
import { ParticipanteService } from '../../../../services/participante.service';
import { TareaService } from '../../../../services/tarea.service';
import { ToastService, SnackbarStyles } from '../../../../commons/toast.service';
import { AppTitleService } from '../../../../services/app-title.service';
import { DateService } from '../../../../shared/date.service';
import { AuthService } from '../../../../services/auth.service';
import { UtilService } from '../../../../services/util.service';
import { DlgReasignacionMasivaComponent } from './dlg-reasignacion-masiva/dlg-reasignacion-masiva.component';
import { MatDialog } from '@angular/material/dialog';
import { UltimoUsoPipe } from '../../../../pipes/ultimo-uso.pipe';
import { MatToolbarModule } from '@angular/material/toolbar';

@Component({
    selector: 'app-reasignacion-masiva',
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        MatChipsModule,
        MatIconModule,
        MatButtonModule,
        MatProgressBarModule,
        UltimoUsoPipe,
        MatProgressSpinnerModule,
        MatToolbarModule,
        AsyncPipe,
    ],
    templateUrl: './reasignacion-masiva.component.html',
    styleUrl: './reasignacion-masiva.component.scss'
})
export class ReasignacionMasivaComponent implements OnInit {
    allUsers: ServidorPublico[] = [];

    reasignarForm = new FormGroup({
        origen: new FormControl<ServidorPublico | string>('', [Validators.required, this.userValidator]),
        destinatario: new FormControl<ServidorPublico | string>('', [Validators.required, this.userValidator]),
        comentario: new FormControl('', [Validators.required])
    });

    filteredOrigenUsers: Observable<ServidorPublico[]>;
    filteredDestinoUsers: Observable<ServidorPublico[]>;
    
    progressValue = 0;
    progressMessage = '';
    token: any;

    constructor(
        private participanteService: ParticipanteService,
        private utilService: UtilService,
        private toast: ToastService,
        private appTitleService: AppTitleService,
        private dialog: MatDialog,
        private authService: AuthService
    ) {
        this.appTitleService.setTitle("Administraci\xf3n");
        this.token = this.authService.getTokenData();

        this.filteredOrigenUsers = this.reasignarForm.controls.origen.valueChanges.pipe(
            startWith(''),
            map(value => {
                const name = typeof value === 'string' ? value : value?.nombres;
                return name ? this._filter(name as string) : this.allUsers.slice();
            }),
        );

        this.filteredDestinoUsers = this.reasignarForm.controls.destinatario.valueChanges.pipe(
            startWith(''),
            map(value => {
                const name = typeof value === 'string' ? value : value?.nombres;
                return name ? this._filter(name as string) : this.allUsers.slice();
            }),
        );
    }

    ngOnInit(): void {
        this.loadUsers();
    }

    loadUsers() {
        
        this.participanteService.todosSp().subscribe({
            next: (users) => {
                this.allUsers = users.sort((a, b) => (a.apellidos > b.apellidos ? 1 : -1));
                this.reasignarForm.controls.origen.setValue(''); // Trigger filter update
                this.reasignarForm.controls.destinatario.setValue(''); // Trigger filter update
            },
            error: (err) => {
                console.error('Error loading users', err);
                this.toast.show({ message: 'Error cargando usuarios', style: SnackbarStyles.Error });
            }
        });
    }

    private _filter(value: string): ServidorPublico[] {
        const filterValue = value.toLowerCase();

        return this.allUsers.filter(user =>
            user.nombres?.toLowerCase().includes(filterValue) ||
            user.apellidos?.toLowerCase().includes(filterValue) ||
            user.cargo?.toLowerCase().includes(filterValue)
        );
    }

    displayFn(user: ServidorPublico): string {
        return user && user.nombres ? `${user.apellidos} ${user.nombres} ` : '';
    }

    userValidator(control: FormControl): { [key: string]: boolean } | null {
        if (typeof control.value === 'string' && control.value.length > 0) {
            return { 'invalidUser': true };
        }
        return null;
    }

    async onReasignar() {
        if (this.reasignarForm.invalid) return;

        const origen = this.reasignarForm.controls.origen.value as ServidorPublico;
        const destinatario = this.reasignarForm.controls.destinatario.value as ServidorPublico;
        const comentario = this.reasignarForm.controls.comentario.value;
        
        if (origen.id === destinatario.id) {
            this.toast.show({ message: 'El usuario de destino no puede ser el mismo que el de origen', style: SnackbarStyles.Warning });
            return;
        }
        
        this.getContadorDocumentUser(origen.id);
    }

    public getContadorDocumentUser(userId: number) {
        this.utilService.getNumeroTareasUsuario(userId).subscribe({
            next: (value) => {                
                this.showDialogReasignacionMasiva(value, {origen: this.reasignarForm.controls.origen.value, destinatario: this.reasignarForm.controls.destinatario.value, comentario: this.reasignarForm.controls.comentario.value});                  
            },
            error: (err) => {
                console.error('Error loading users', err);                
                this.toast.show({ message: 'Error cargando usuarios', style: SnackbarStyles.Error });
            }
        });
    }

    private showDialogReasignacionMasiva(totalDocumentos: any, data: any) {
        const dialogRef = this.dialog.open(DlgReasignacionMasivaComponent, {
            data: {
                contador: totalDocumentos,
                data: data
            }
        }).afterClosed().subscribe(result => {            
            if (result) {
                this.resetForm();
            }            
        });        
    }

    resetForm() {
        this.reasignarForm.reset();
        this.reasignarForm.controls.origen.setValue('');
        this.reasignarForm.controls.destinatario.setValue('');
        this.reasignarForm.controls.comentario.setValue('');
    }
}

